import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import XLSX from 'xlsx'

import FilterSearch from 'pages/parts/FilterSearch'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { httpOk, GET } from 'helpers/api'
import {
	parseProductTableOrderBy,
	buildProductTableOrderBy,
} from 'helpers/productFilterParams'
import { renderSortIndicator, renderSortDirectionIcon } from 'helpers/tableSortIndicator'
import { formatMoney, returnSign, formatDate, findKeyFromArrayByValue } from 'helpers/helpers'
import CustomTooltip from "pages/parts/CustomTooltip"
import { businessNow, businessDayStartOf, businessDayEndOf } from 'helpers/businessDay'

function toExcelNumber(value) {
	if (value === null || value === undefined || value === '') return ''
	const num = Number(value)
	return Number.isNaN(num) ? value : num
}

function compareGrossIncomeRows(a, b, field, sortDir) {
	const mul = sortDir === "desc" ? -1 : 1
	let va = a[field]
	let vb = b[field]
	if (va == null) va = ""
	if (vb == null) vb = ""
	if (field === "name") {
		return mul * String(va).localeCompare(String(vb), undefined, { sensitivity: "base", numeric: true })
	}
	const na = Number(va)
	const nb = Number(vb)
	if (!Number.isNaN(na) && !Number.isNaN(nb)) {
		if (na === nb) return 0
		return mul * (na < nb ? -1 : 1)
	}
	return mul * String(va).localeCompare(String(vb), undefined, { numeric: true })
}

function sortGrossIncomeDataRows(rows, orderByStr) {
	const copy = [...(rows || [])]
	if (!orderByStr) return copy
	const { field, sort } = parseProductTableOrderBy(orderByStr)
	if (!field) return copy
	copy.sort((a, b) => compareGrossIncomeRows(a, b, field, sort))
	return copy
}

function Products() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const orderTypes = [
		{ "id": 1, "name": t('hall') },
		{ "id": 2, "name": t('takeaway') },
		{ "id": 3, "name": t('delivery') },
	]

	const [data, setData] = useState({
		'data': []
	})
	const [menu, setMenu] = useState([])
	const [poses, setPoses] = useState([])
	const [shifts, setShifts] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState(() => {
		// Месяц до конца сегодняшнего операционного дня точки (при 03:00 — до 02:59 следующего дня)
		const today = businessNow()
		const monthStart = businessDayStartOf(new Date(today.getFullYear(), today.getMonth(), 1))
		const todayEnd = businessDayEndOf(today)
		return {
			'start_date': formatDate(monthStart, 'yyyy-MM-dd HH:mm:ss'),
			'end_date': formatDate(todayEnd, 'yyyy-MM-dd HH:mm:ss'),
			'pos_id': reduxAccount.pos_id,
			'search': '',
			'order_by': null,
			'filter_type': 'day',
			'shift_id': '',
		}
	})

	const sortedTableRows = useMemo(
		() => sortGrossIncomeDataRows(data?.data, filterData.order_by),
		[data?.data, filterData.order_by],
	)

	const appliedOrderParsed = parseProductTableOrderBy(filterData.order_by)

	function grossIncomeSortStateForIndicator(fd) {
		const { field, sort } = parseProductTableOrderBy(fd.order_by)
		return { order_by: field, sort }
	}

	function grossIncomeSortFieldLabel(field) {
		if (field === "name") return t("name2")
		if (field === "total_quantity") return t("quantity")
		if (field === "price") return t("avg_cost_price")
		if (field === "sale_price") return t("avg_sale_price")
		if (field === "total_price") return t("cost_price_amount")
		if (field === "total_sale_price") return t("sales_amount")
		if (field === "total_income") return t("gross_income")
		if (field === "percent") return "%"
		return field
	}

	function handleGrossIncomeTableSort(fieldKey) {
		const { field: currentField, sort: currentSort } = parseProductTableOrderBy(filterData.order_by)
		const nextSort = currentField === fieldKey ? (currentSort === "asc" ? "desc" : "asc") : "asc"
		const order_by = buildProductTableOrderBy(fieldKey, nextSort)
		setFilterData({ ...filterData, order_by })
	}

	async function searchWithFilter(exportToExcel = false, tableSort = "", nextFilterData = null) {
		var filterUrl = "/report-gross-income"
		var urlParams = ""
		checkFilter()
		const currentFilterData = nextFilterData || filterData

		if (currentFilterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + currentFilterData.pos_id
		if (currentFilterData.menu_ids && currentFilterData.menu_ids.length > 0) {
			const ids = currentFilterData.menu_ids.map(item => item.id).join(',');
			urlParams += returnSign(urlParams) + 'menu_ids=' + ids;
		}
		if (currentFilterData.order_type_id)
			urlParams += returnSign(urlParams) + 'order_type_id=' + currentFilterData.order_type_id
		if (currentFilterData.filter_type)
			urlParams += returnSign(urlParams) + 'filter_type=' + currentFilterData.filter_type
		if (currentFilterData.filter_type === 'shift' && currentFilterData.shift_id)
			urlParams += returnSign(urlParams) + 'shift_id=' + currentFilterData.shift_id
		if (currentFilterData.filter_type !== 'shift' && currentFilterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + currentFilterData.start_date
		if (currentFilterData.filter_type !== 'shift' && currentFilterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + currentFilterData.end_date
		if (currentFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + currentFilterData.search

		if (`/report-gross-income${urlParams}` !== filterUrl && !exportToExcel)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		if (exportToExcel) {
			var excelHeaders = [[
				t('name2'),
				t('quantity'),
				t('avg_cost_price'),
				t('avg_sale_price'),
				t('cost_price_amount'),
				t('sales_amount'),
				t('gross_income'),
				'%',
			]]
			const rowsForExcel = sortGrossIncomeDataRows(data?.data, currentFilterData.order_by)

			for (let i = 0; i < rowsForExcel.length; i++) {
				const r = rowsForExcel[i]
				var excelItems = []
				excelItems.push(r['name'])
				excelItems.push(toExcelNumber(r['total_quantity'] ?? r['quantity']))
				excelItems.push(toExcelNumber(r['price']))
				excelItems.push(toExcelNumber(r['sale_price']))
				excelItems.push(toExcelNumber(r['total_price']))
				excelItems.push(toExcelNumber(r['total_sale_price']))
				excelItems.push(toExcelNumber(r['total_income'] ?? r['income']))
				excelItems.push(toExcelNumber(r['percent']))
				excelHeaders.push(excelItems)
			}

			excelHeaders.push([
				t('total'),
				toExcelNumber(data?.total_quantity),
				'',
				'',
				toExcelNumber(data?.total_price),
				toExcelNumber(data?.total_sale_price),
				toExcelNumber(data?.total_income),
				100,
			])

			const ws = XLSX.utils.aoa_to_sheet(excelHeaders);
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
			XLSX.writeFile(wb, t('gross_income') + ".xlsx");
		} else {
			const response = await GET(filterUrl, {}, { loader: true })
			if (httpOk(response)) setData(response.data)
		}

		setDropdownIsOpen(false)
	}

	function handleSearchChange(search) {
		const nextFilterData = { ...filterData, search, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(false, "", nextFilterData)
	}

	function resetFilter() {
		// Месяц до конца сегодняшнего операционного дня точки (при 03:00 — до 02:59 следующего дня)
		const today = businessNow()
		const monthStart = businessDayStartOf(new Date(today.getFullYear(), today.getMonth(), 1))
		const todayEnd = businessDayEndOf(today)
		const nextFilterData = {
			start_date: formatDate(monthStart, 'yyyy-MM-dd HH:mm:ss'),
			end_date: formatDate(todayEnd, 'yyyy-MM-dd HH:mm:ss'),
			pos_id: null,
			search: '',
			order_by: null,
			filter_type: 'day',
			shift_id: '',
		}
		setFilterData(nextFilterData)
		searchWithFilter(false, "", nextFilterData)
		setFilterDataExist(false)
	}

	function checkFilter() {
		if (
			filterData.pos_id === null &&
			filterData.start_date === null &&
			filterData.end_date === null &&
			filterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function changeFilterType(filterType) {
		setFilterData({
			...filterData,
			filter_type: filterType,
			shift_id: filterType === 'day' ? '' : filterData.shift_id,
		})
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getShifts() {
		let filterUrl = "/helper/shifts?limit=20"
		if (filterData.pos_id) {
			filterUrl += `&pos_id=${filterData.pos_id}`
		}
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) setShifts(response.data)
	}

	async function getMenu() {
		const response = await GET('/helper/menu', {}, { loader: true })
		if (httpOk(response)) setMenu(response.data)
	}

	useEffect(() => {
		getMenu()
		getPoses()
		searchWithFilter(false)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('gross_income')} [{data?.data?.length}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={handleSearchChange}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => {
														setShifts([])
														setFilterData({ ...filterData, 'pos_id': option.id, shift_id: '' })
													}}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('order_types')}</label>
												<Select
													options={orderTypes}
													value={orderTypes.find(option => option.id === filterData.order_type_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'order_type_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('menu')}</label>
												<Select
													isMulti
													isClearable
													options={menu}
													value={filterData.menu_ids}
													onChange={(options) => setFilterData({ ...filterData, 'menu_ids': options ? options : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('filter')}</label>
												<div className="d-flex flex-wrap gap-3">
													<div className="form-check">
														<input
															className="form-check-input"
															type="checkbox"
															id="gross-income-filter-day"
															checked={filterData.filter_type !== 'shift'}
															onChange={() => changeFilterType('day')}
														/>
														<label className="form-check-label" htmlFor="gross-income-filter-day">
															{t('day')}
														</label>
													</div>
													<div className="form-check">
														<input
															className="form-check-input"
															type="checkbox"
															id="gross-income-filter-shift"
															checked={filterData.filter_type === 'shift'}
															onChange={() => changeFilterType('shift')}
														/>
														<label className="form-check-label" htmlFor="gross-income-filter-shift">
															{t('shift')}
														</label>
													</div>
												</div>
											</div>
										</div>
										{filterData.filter_type === 'shift' && (
											<div className="row">
												<div className="form-group col-md-6">
													<label>{t('shift')} ID</label>
													<input
														type="text"
														className="form-control"
														value={filterData.shift_id || ''}
														onChange={(e) => setFilterData({ ...filterData, shift_id: e.target.value })}
													/>
												</div>
												<div className="form-group col-md-6">
													<label className="invisible">.</label>
													<button type="button" className="btn btn-outline-primary w-100" onClick={getShifts}>
														{t('load')}
													</button>
												</div>
												{shifts.length > 0 && (
													<div className="form-group col-md-12">
														<Select
															options={shifts}
															value={shifts.find(option => String(option.id) === String(filterData.shift_id)) || ''}
															onChange={(option) => setFilterData({ ...filterData, shift_id: option ? option.id : '' })}
															placeholder=""
															noOptionsMessage={() => t('list_empty')}
															getOptionLabel={(option) => `#${option.id} ${option.pos_name || ''} ${formatDate(option.opened_at, 'dd.MM.yy HH:mm')} - ${option.closed_at ? formatDate(option.closed_at, 'dd.MM.yy HH:mm') : '...'}`}
															getOptionValue={(option) => option.id}
														/>
													</div>
												)}
											</div>
										)}
										{filterData.filter_type !== 'shift' && <div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>}
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter(false)}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2" onClick={() => searchWithFilter(true)}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>
						<div className="vertical-center">
							<h6 className="d-flex justify-content-between gap-3">
								<b>{t('amount')}:</b> {formatMoney(data?.total_income)}
							</h6>
							<h6 className="d-flex justify-content-between gap-3">
								<b>{t('allowances')}:</b> {formatMoney(data?.total_allowances)}
							</h6>
						</div>
					</div>

					{(filterDataExist || filterData.order_by) &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{filterData.filter_type === 'shift' && filterData.shift_id &&
									<span className="me-2">
										<b>{t('shift')} ID: </b>{filterData.shift_id}
									</span>
								}
								{filterData.filter_type !== 'shift' && (filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date, 'dd.MM.yy HH:mm')}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<span>|</span>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date, 'dd.MM.yy HH:mm')}</span>
										}
									</span>
								}
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{(filterData.order_type_id) &&
									<span className="me-2">
										<b>{t('order_types')}: </b>
										{findKeyFromArrayByValue(orderTypes, 'id', filterData.order_type_id, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
								{filterData.order_by && appliedOrderParsed.field && (
									<span className="me-2 d-inline-flex align-items-center flex-wrap gap-1">
										<b>{t("sort_by")}: </b>
										<span className="d-inline-flex align-items-center gap-1">
											{grossIncomeSortFieldLabel(appliedOrderParsed.field)}
											{renderSortDirectionIcon(appliedOrderParsed.sort, {
												ariaLabel:
													appliedOrderParsed.sort === "desc"
														? t("sort_descending")
														: t("sort_ascending"),
												title:
													appliedOrderParsed.sort === "desc"
														? t("sort_descending")
														: t("sort_ascending"),
											})}
										</span>
									</span>
								)}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th
										style={{ width: "100%", minWidth: "8rem" }}
										className="th-sortable user-select-none"
										onClick={() => handleGrossIncomeTableSort("name")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-between gap-2 pe-1 w-100 min-w-0">
											<span className="text-truncate min-w-0" title={t('name2')}>{t('name2')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("name", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "6.5rem" }}
										className="text-end th-sortable user-select-none"
										onClick={() => handleGrossIncomeTableSort("total_quantity")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="text-truncate flex-grow-1 min-w-0 text-end" title={t('quantity')}>{t('quantity')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("total_quantity", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "11rem" }}
										className="text-end th-sortable user-select-none"
										title={t('avg_cost_price')}
										onClick={() => handleGrossIncomeTableSort("price")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="text-truncate flex-grow-1 min-w-0 text-end" title={t('avg_cost_price')}>{t('avg_cost_price')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("price", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "11rem" }}
										className="text-end th-sortable user-select-none"
										title={t('avg_sale_price')}
										onClick={() => handleGrossIncomeTableSort("sale_price")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="text-truncate flex-grow-1 min-w-0 text-end" title={t('avg_sale_price')}>{t('avg_sale_price')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("sale_price", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "11rem" }}
										className="text-end th-sortable user-select-none"
										title={t('cost_price_amount')}
										onClick={() => handleGrossIncomeTableSort("total_price")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="text-truncate flex-grow-1 min-w-0 text-end" title={t('cost_price_amount')}>{t('cost_price_amount')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("total_price", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "10rem" }}
										className="text-end th-sortable user-select-none"
										title={t('sales_amount')}
										onClick={() => handleGrossIncomeTableSort("total_sale_price")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="text-truncate flex-grow-1 min-w-0 text-end" title={t('sales_amount')}>{t('sales_amount')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("total_sale_price", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "10rem" }}
										className="text-end th-sortable user-select-none"
										title={t('gross_income')}
										onClick={() => handleGrossIncomeTableSort("total_income")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="text-truncate flex-grow-1 min-w-0 text-end" title={t('gross_income')}>{t('gross_income')}</span>
											<span className="flex-shrink-0">{renderSortIndicator("total_income", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
									<th
										style={{ minWidth: "33px" }}
										className="text-end th-sortable user-select-none"
										onClick={() => handleGrossIncomeTableSort("percent")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-1 w-100 min-w-0">
											<span className="flex-shrink-0">%</span>
											<span className="flex-shrink-0">{renderSortIndicator("percent", grossIncomeSortStateForIndicator(filterData))}</span>
										</div>
									</th>
								</tr>
							</thead>
							<tbody>
								{sortedTableRows.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td>{item.name}</td>
										<td className="text-end">{formatMoney(item.total_quantity)}</td>
										<td className="text-end">{formatMoney(item.price)}</td>
										<td className="text-end">{formatMoney(item.sale_price)}</td>
										<td className="text-end">{formatMoney(item.total_price)}</td>
										<td className="text-end">{formatMoney(item.total_sale_price)}</td>
										<td className="text-end">{formatMoney(item.total_income)}</td>
										<td className="text-end">{formatMoney(item.percent, 2)}</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td className="text-end"><b>{formatMoney(data.total_quantity)}</b></td>
									<td></td>
									<td></td>
									<td className="text-end"><b>{formatMoney(data.total_price)}</b></td>
									<td className="text-end"><b>{formatMoney(data.total_sale_price)}</b></td>
									<td className="text-end"><b>{formatMoney(data.total_income)}</b></td>
									<td className="text-end"><b>100</b></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default Products